import * as React from 'react';
import Grid from '@material-ui/core/Grid';
import TranslateIcon from '@material-ui/icons/Translate';
import MenuItem from '@material-ui/core/MenuItem';
import Select from '@material-ui/core/Select';
import { applyLocalePreference } from '../helpers/localePreference';
import { LanguageSwitcherParent } from '../types';
import {
  additionalLanguageOptions,
  displayNameTypes,
  languageOptions,
  languageSwitcherParentTypes
} from '../constants';
import { makeStyles, Theme } from '@material-ui/core/styles';
import { StylesProvider, createGenerateClassName } from '@material-ui/core/styles';
import { useTranslation } from 'react-i18next';
import { createIntl } from '@formatjs/intl';
import { useFeature } from 'flagged';

type Props = {
  typography?: string;
  parent?: LanguageSwitcherParent;
  userId?: string;
  iconVisible?: boolean;
};

const generateClassName = createGenerateClassName({
  seed: 'aion-ui-i18next',
  productionPrefix: 'aion-ui-i18next'
});

const useHeaderStyles = makeStyles((theme: Theme) => ({
  languageSwitcherContainer: {
    marginRight: 0,
    marginLeft: theme.spacing(4),
    justifyContent: 'flex-end',
    alignItems: 'center',
    display: 'flex',
    width: 'auto'
  },
  select: {
    color: theme.palette.common.white,
    fontSize: theme.typography.pxToRem(14),
    '&::before, &::after': {
      display: 'none'
    }
  },
  selectEl: {
    paddingBottom: '0.25rem',
    '&:focus': {
      backgroundColor: 'transparent'
    }
  },
  icon: {
    fill: theme.palette.common.white
  },
  languageIcon: {
    fill: theme.palette.common.white
  },
  iconContainer: {
    display: 'flex',
    marginRight: '0.25rem'
  },
  languageText: {
    textTransform: 'capitalize'
  }
}));

const useFooterStyles = makeStyles((theme: Theme) => ({
  languageSwitcherContainer: {
    alignItems: 'center',
    marginBottom: theme.spacing(1),
    justifyContent: 'center',
    display: 'flex',
    width: 'auto'
  },
  select: {
    fontSize: theme.typography.pxToRem(14),
    '&::before, &::after': {
      display: 'none'
    }
  },
  selectEl: {
    paddingBottom: '0.25rem',
    '&:focus': {
      backgroundColor: 'transparent'
    }
  },
  icon: {
    fill: theme.palette.text.secondary
  },
  languageIcon: {
    fill: theme.palette.text.secondary
  },
  iconContainer: {
    display: 'flex',
    marginRight: '0.25rem'
  },
  languageText: {
    textTransform: 'capitalize'
  }
}));

const useRegistrationStyles = makeStyles((theme: Theme) => ({
  languageSwitcherContainer: {
    alignItems: 'center',
    marginBottom: theme.spacing(1),
    justifyContent: 'center',
    display: 'flex',
    width: 'auto'
  },
  select: {
    color: theme.palette.common.white,
    fontSize: theme.typography.pxToRem(14),
    '&::before, &::after': {
      display: 'none'
    }
  },
  selectEl: {
    paddingBottom: '0.25rem',
    '&:focus': {
      backgroundColor: 'transparent'
    }
  },
  icon: {
    fill: theme.palette.common.white
  },
  languageIcon: {
    fill: theme.palette.common.white
  },
  iconContainer: {
    display: 'flex',
    marginRight: '0.25rem'
  },
  languageText: {
    textTransform: 'capitalize'
  }
}));

export const LanguageSwitcherComponent: React.FC<Props> = ({
  parent = languageSwitcherParentTypes.header,
  userId,
  iconVisible = true
}) => {
  const { i18n } = useTranslation();
  const header = useHeaderStyles();
  const footer = useFooterStyles();
  const registration = useRegistrationStyles();
  const isExtendedLocalesEnabled = useFeature('EnableExtendedLocales');
  const classes = {
    header,
    footer,
    registration
  }[parent];

  const onLanguageChange = async (e: React.ChangeEvent<{ value: unknown }>) => {
    const lng = e.target.value as string;

    await applyLocalePreference(lng, userId, true);
  };

  const currentLanguage = i18n.language === 'en' ? 'en-US' : i18n.language;

  return (
    <StylesProvider generateClassName={generateClassName}>
      <Grid className={classes.languageSwitcherContainer}>
        {iconVisible && (
          <Grid className={classes.iconContainer}>
            <TranslateIcon className={classes.languageIcon} />
          </Grid>
        )}
        <Grid item>
          <Select
            className={classes.select}
            name="language-switcher"
            onChange={onLanguageChange}
            value={currentLanguage}
            inputProps={{
              classes: {
                icon: classes.icon,
                select: classes.selectEl
              }
            }}
          >
            {[...languageOptions, ...(isExtendedLocalesEnabled ? additionalLanguageOptions : [])].map(
              ({ value, languageCode, countryCode }) => {
                const intl = createIntl({
                  locale: value,
                  messages: {}
                });
                return (
                  <MenuItem value={value} key={value} data-value={value}>
                    <span className={classes.languageText}>
                      {intl.formatDisplayName(languageCode, { type: displayNameTypes.language })}
                    </span>
                    <span>&nbsp;({intl.formatDisplayName(countryCode, { type: displayNameTypes.region })})</span>
                  </MenuItem>
                );
              }
            )}
          </Select>
        </Grid>
      </Grid>
    </StylesProvider>
  );
};
